/**
 * Display formatting (spec §7): Arabic labels, 24-hour time, and status that is
 * always carried by words and an icon rather than colour alone.
 */

const TIME_FMT = new Intl.DateTimeFormat('ar-IQ-u-nu-latn', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const DATETIME_FMT = new Intl.DateTimeFormat('ar-IQ-u-nu-latn', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export function formatDateTime(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const fmt = timeZone
    ? new Intl.DateTimeFormat('ar-IQ-u-nu-latn', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone,
      })
    : DATETIME_FMT;
  return fmt.format(d);
}

export function formatTime(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const fmt = timeZone
    ? new Intl.DateTimeFormat('ar-IQ-u-nu-latn', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
        timeZone,
      })
    : TIME_FMT;
  return fmt.format(d);
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'لم يحدث بعد';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const abs = Math.abs(ms);
  const future = ms < 0;
  const units: Array<[number, string, string]> = [
    [86_400_000, 'يوم', 'أيام'],
    [3_600_000, 'ساعة', 'ساعات'],
    [60_000, 'دقيقة', 'دقائق'],
    [1000, 'ثانية', 'ثوانٍ'],
  ];
  for (const [size, one, many] of units) {
    if (abs >= size) {
      const n = Math.floor(abs / size);
      const word = n === 1 ? one : n === 2 ? `${one}ان` : many;
      return future ? `بعد ${n} ${word}` : `قبل ${n} ${word}`;
    }
  }
  return 'الآن';
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} بايت`;
  const units = ['ك.ب', 'م.ب', 'غ.ب', 'ت.ب'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[i]}`;
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} م.ث`;
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds} ث`;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/* ------------------------------------------------------------ vocabulary */

export const WEEKDAYS = [
  { value: 0, label: 'الأحد', short: 'ح' },
  { value: 1, label: 'الاثنين', short: 'ن' },
  { value: 2, label: 'الثلاثاء', short: 'ث' },
  { value: 3, label: 'الأربعاء', short: 'ر' },
  { value: 4, label: 'الخميس', short: 'خ' },
  { value: 5, label: 'الجمعة', short: 'ج' },
  { value: 6, label: 'السبت', short: 'س' },
] as const;

export const ROLE_LABELS: Record<string, string> = {
  PLATFORM_ADMIN: 'مسؤول المنصة',
  TENANT_ADMIN: 'مدير المديرية',
  TENANT_OPERATOR: 'مشغّل المديرية',
  SCHOOL_ADMIN: 'مسؤول المدرسة',
  VIEWER: 'مراقب',
};

export const PERMISSION_LABELS: Record<string, string> = {
  'incident.activate': 'تفعيل الإنذار',
  'incident.clear': 'إلغاء الإنذار',
  'playback.manual': 'تشغيل يدوي',
  'schedule.publish': 'نشر الجداول',
  'device.revoke': 'إلغاء الأجهزة',
  'backup.manage': 'النسخ الاحتياطي',
  'firmware.manage': 'إدارة الإصدارات',
};

export const READINESS: Record<string, { label: string; tone: string; icon: string }> = {
  ready: { label: 'جاهز', tone: 'ok', icon: '✔' },
  degraded: { label: 'يعمل مع ملاحظات', tone: 'warn', icon: '▲' },
  not_ready: { label: 'غير جاهز', tone: 'danger', icon: '✖' },
  unknown: { label: 'غير معروف', tone: 'muted', icon: '?' },
};

export const DEPLOY_STAGE: Record<string, { label: string; tone: string; icon: string }> = {
  PENDING: { label: 'بانتظار الإشعار', tone: 'muted', icon: '…' },
  NOTIFIED: { label: 'أُشعر', tone: 'info', icon: '→' },
  DOWNLOADING: { label: 'قيد التنزيل', tone: 'info', icon: '↓' },
  APPLIED: { label: 'طُبّق', tone: 'ok', icon: '✔' },
  FAILED: { label: 'فشل', tone: 'danger', icon: '✖' },
  SUPERSEDED: { label: 'استُبدل', tone: 'muted', icon: '↺' },
};

export const AUDIO_STATE: Record<string, { label: string; tone: string; icon: string }> = {
  UPLOADED: { label: 'مرفوع', tone: 'muted', icon: '…' },
  TRANSCODING: { label: 'قيد التحويل', tone: 'info', icon: '↻' },
  READY: { label: 'جاهز', tone: 'ok', icon: '✔' },
  FAILED: { label: 'فشل التحويل', tone: 'danger', icon: '✖' },
};

export const FAULT_LABELS: Record<string, string> = {
  SCHEDULE_EXPIRED: 'انتهت صلاحية الجدول المحلي',
  CLOCK_UNRELIABLE: 'الساعة غير موثوقة',
  STORAGE_MISSING: 'بطاقة التخزين غير موجودة',
  STORAGE_LOW: 'المساحة منخفضة',
  STORAGE_ERROR: 'خطأ في التخزين',
  AUDIO_OUTPUT_ERROR: 'خطأ في الإخراج الصوتي',
  MANIFEST_INVALID: 'بيان الجدول غير صالح',
  ASSET_MISSING: 'ملف صوتي ناقص',
  LOG_OVERFLOW: 'امتلاء السجل المحلي',
  OTA_FAILED: 'فشل تحديث البرنامج',
};

export const DEVICE_EVENT_LABELS: Record<string, string> = {
  boot: 'إقلاع',
  schedule_event_claimed: 'حجز حدث مجدول',
  schedule_event_started: 'بدأ الإخراج الصوتي لحدث مجدول',
  schedule_event_completed: 'انتهى الإخراج الصوتي',
  schedule_event_missed: 'حدث فائت لم يُعوَّض',
  schedule_event_failed: 'فشل تنفيذ حدث',
  manual_play_started: 'بدأ تشغيل يدوي',
  manual_play_completed: 'انتهى التشغيل اليدوي',
  incident_activated: 'تفعيل حادثة',
  incident_cleared: 'إلغاء حادثة',
  button_pressed: 'ضغط زر محلي',
  external_alarm_changed: 'تغيّر مدخل الإنذار الخارجي',
  manifest_applied: 'تطبيق بيان جدول',
  manifest_rejected: 'رفض بيان جدول',
  clock_set: 'ضبط الساعة',
  clock_lost: 'فقدان الساعة',
  storage_error: 'خطأ تخزين',
  audio_output_error: 'خطأ إخراج صوتي',
  ota_started: 'بدء تحديث',
  ota_result: 'نتيجة تحديث',
  log_overflow: 'فقدان سجلات بسبب الامتلاء',
  // Bell device (esp32-dfplayer-bell)
  enrolled: 'تسجيل الجهاز في المنصة',
  day_started: 'بدء اليوم الدراسي',
  day_finished: 'انتهاء اليوم الدراسي',
  day_reset: 'إيقاف اليوم الدراسي',
  lesson_started: 'بداية حصة',
  lesson_ended: 'نهاية حصة',
  break_started: 'بداية استراحة',
  skipped: 'تخطي المرحلة',
  silent_advance: 'تقدّم الجدول بصمت أثناء الطوارئ',
  safety_advance: 'تقديم المرحلة (قاعدة 10 ثوانٍ)',
  audio_started: 'بدء تشغيل مقطع',
  emergency_started: 'بدء الطوارئ',
  emergency_cancelled: 'إلغاء الطوارئ',
  emergency_end_finished: 'انتهاء رسالة العودة',
  auto_start_skipped: 'لم يبدأ اليوم تلقائيًا',
  auto_start_deferred: 'تأجيل البدء التلقائي',
  config_applied: 'تطبيق إعدادات جديدة',
  rtc_adjusted: 'ضبط ساعة RTC',
  player_event: 'حدث من مشغل MP3',
  hardware_fault: 'عطل في العتاد',
  hardware_recovered: 'عودة العتاد للعمل',
  volume_changed: 'تغيير مستوى الصوت',
  sound_downloaded: 'تنزيل صوت إلى بطاقة الجهاز',
  sounds_synced: 'اكتملت مزامنة الأصوات',
  sound_download_failed: 'فشل تنزيل صوت',
  storage_low: 'بطاقة الذاكرة ممتلئة',
  schedule_joined: 'انضمام للجدول الجاري بعد تشغيل الجهاز',
  wifi_changed: 'تغيير شبكة WiFi',
  wifi_change_failed: 'فشل تغيير الشبكة (عاد للسابقة)',
  wifi_fallback: 'التحويل إلى الشبكة الاحتياطية',
  wifi_scanned: 'البحث عن الشبكات',
  ota_rolled_back: 'العودة للإصدار السابق بعد تحديث فاشل',
  audio_timeout: 'مقطع لم يُبلَّغ عن انتهائه فتجاوزه الجهاز (قد يكون غير موجود على البطاقة)',
  once_failed: 'فشل البث لمرة واحدة',
  sounds_checked: 'فحص بطاقة الأصوات',
  sound_deleted: 'حذف مقطع من البطاقة',
};

export const BELL_COMMAND_LABELS: Record<string, string> = {
  'bell.start_day': 'بدء اليوم الدراسي',
  'bell.reset_day': 'إيقاف اليوم الدراسي',
  'bell.skip': 'تخطي',
  'bell.play_anthem': 'النشيد الكامل',
  'bell.stop_audio': 'إيقاف التشغيل اليدوي',
  'bell.play_track': 'تشغيل مقطع',
  'bell.start_lesson': 'بدء حصة',
  'bell.set_volume': 'مستوى الصوت',
  'bell.start_emergency': 'تفعيل طوارئ الجهاز',
  'bell.cancel_emergency': 'إلغاء طوارئ الجهاز',
  'bell.sync_time': 'مزامنة الساعة',
  'bell.reboot': 'إعادة التشغيل',
  'bell.request_status': 'طلب حالة',
  'bell.set_wifi': 'تغيير شبكة WiFi',
  'bell.scan_wifi': 'البحث عن الشبكات',
  'bell.ota_update': 'تحديث البرنامج',
  'bell.play_once': 'بث مقطع لمرة واحدة',
  'bell.sync_assets': 'فحص ومزامنة أصوات البطاقة',
  'bell.delete_track': 'حذف مقطع من البطاقة',
  activate_incident: 'تفعيل حادثة طوارئ',
  clear_incident: 'إلغاء حادثة طوارئ',
  request_status: 'طلب حالة',
};

export const BELL_ACK_CODE_LABELS: Record<string, string> = {
  ok: 'تم',
  duplicate: 'مكرر (نُفّذ سابقًا)',
  expired: 'انتهت صلاحيته',
  busy_emergency: 'مرفوض: الطوارئ نشطة',
  busy_normal: 'مرفوض: إعلان مدرسي قيد التشغيل',
  not_running: 'مرفوض: لا يوجد عداد يعمل',
  nothing_to_stop: 'لا يوجد تشغيل يدوي لإيقافه',
  not_active: 'لا توجد طوارئ نشطة',
  invalid_args: 'قيمة غير صالحة',
  hardware_fault: 'عطل عتاد (MP3 أو RTC)',
  pending: 'مؤجل حتى انتهاء رسالة العودة',
  incident_already_cleared: 'الحادثة أُلغيت سابقًا',
  unsupported_type: 'غير مدعوم على هذا الجهاز',
  wifi_failed: 'تعذر الاتصال بالشبكة الجديدة (بقي على السابقة)',
  ota_failed: 'فشل التحديث (بقي على الإصدار الحالي)',
  ota_busy: 'مرفوض: الجرس يعمل الآن',
  same_version: 'يعمل بهذا الإصدار مسبقًا',
  downloading: 'الجهاز ينزّل المقطع',
  sd_missing: 'بطاقة الذاكرة غير موجودة في الجهاز',
  sd_full: 'بطاقة الذاكرة ممتلئة',
  download_failed: 'تعذر تنزيل المقطع',
  sha_mismatch: 'وصل الملف تالفًا',
  busy_download: 'بث آخر قيد التنزيل',
  busy_playing: 'المقطع قيد التشغيل الآن',
  needs_sd_card: 'يتطلب جهازًا ببطاقة ذاكرة ومخرج I2S',
  checking: 'جارٍ فحص البطاقة',
};

/** Keys and values that appear in bell event details. */
export const BELL_DETAIL_VALUE_LABELS: Record<string, string> = {
  button: 'الزر',
  source: 'المصدر',
  lesson: 'الحصة',
  after_lesson: 'بعد الحصة',
  track: 'المقطع',
  owner: 'النوع',
  reason: 'السبب',
  rev: 'النسخة',
  drift_s: 'الانحراف (ث)',
  incident_id: 'الحادثة',
  phase: 'المرحلة',
  volume: 'الصوت',
  fw: 'الإصدار',
  rtc_ok: 'RTC',
  player_ok: 'MP3',
  code: 'الرمز',
  ssid: 'الشبكة',
  rssi: 'الإشارة',
  skip: 'تخطي',
  skip_long: 'تخطي مطوّل',
  anthem: 'النشيد',
  emergency: 'الطوارئ',
  simulation: 'المحاكاة',
  server: 'المنصة',
  schedule: 'الجدول التلقائي',
  holiday: 'عطلة',
  busy: 'اليوم جارٍ',
  normal: 'تسلسل اليوم',
  full_anthem: 'النشيد الكامل',
  manual: 'يدوي',
  emergency_end: 'رسالة العودة',
  lesson_timer: 'عداد حصة',
  break_timer: 'عداد استراحة',
  card_removed: 'أُزيلت البطاقة',
  card_inserted: 'أُدخلت البطاقة',
  player_error: 'خطأ',
  file_missing: 'الملف غير موجود على البطاقة (تُخطّي)',
  sha_mismatch: 'بصمة الملف لا تطابق (سيُعاد التنزيل)',
  sd_full: 'لا توجد مساحة كافية',
  tracks: 'عدد المقاطع',
};

export const ACK_STAGE_LABELS: Record<string, { label: string; tone: string }> = {
  received: { label: 'وصل الأمر', tone: 'muted' },
  accepted: { label: 'قُبل', tone: 'info' },
  started: { label: 'بدأ التنفيذ', tone: 'info' },
  completed: { label: 'اكتمل', tone: 'ok' },
  rejected: { label: 'مرفوض', tone: 'danger' },
  expired: { label: 'منتهي الصلاحية', tone: 'warn' },
  failed: { label: 'فشل', tone: 'danger' },
};

export const EVENT_KIND_LABELS: Record<string, string> = {
  lesson_start: 'بداية حصة',
  lesson_end: 'نهاية حصة',
  break_start: 'بداية استراحة',
  break_end: 'نهاية استراحة',
  anthem: 'نشيد',
  custom: 'حدث خاص',
};

export const PROBLEM_LABELS: Record<string, string> = {
  NONEXISTENT_LOCAL_TIME: 'وقت محلي غير موجود (توقيت صيفي)',
  AMBIGUOUS_LOCAL_TIME: 'وقت محلي متكرر (توقيت صيفي)',
  MISSING_ASSET: 'ملف صوتي ناقص',
  ASSET_LONGER_THAN_WINDOW: 'الملف أطول من نافذة الحدث',
  OVERLAPPING_EVENTS: 'تعارض بين حدثين',
  EVENT_LIMIT_EXCEEDED: 'تجاوز حد عدد الأحداث',
  INVALID_TEMPLATE: 'قالب غير صالح',
  UNKNOWN_ALTERNATE_TEMPLATE: 'قالب استثناء غير متاح',
  HORIZON_TOO_LONG: 'الأفق أطول من المسموح',
};
